import React, { useState, useEffect } from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveMutable } from 'array-move';

import makeStyles from '@mui/styles/makeStyles';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';

import ColumnsIcon from '@mui/icons-material/ViewColumn';
import DragHandleIcon from '@mui/icons-material/DragHandle';

// Hack to get around overly-restrictive linter
const sortableContainer = SortableContainer;
const sortableElement = SortableElement;
const sortableHandle = SortableHandle;

const DragHandle = sortableHandle(() => (
  <ListItemIcon>
    <DragHandleIcon />
  </ListItemIcon>
));

const SortableItem = sortableElement(({ text, value, onToggle }) => {
  const classes = useTileStyles();
  const [checked, setChecked] = React.useState(value);

  useEffect(() => {
    if (value !== checked) {
      setChecked(value);
    }
  });

  const handleChange = (event) => {
    const isChecked = event.target.checked;
    setChecked(isChecked);
    onToggle(text, isChecked);
  };

  return (
    <ListItem ContainerComponent="div" classes={{ root: classes.listItem }}>
      <FormControlLabel
        classes={{ label: classes.label }}
        control={<Checkbox color="primary" checked={checked} onChange={handleChange} />}
        label={text}
      />
      <ListItemSecondaryAction className={classes.dragHandleIcon}>
        <DragHandle />
      </ListItemSecondaryAction>
    </ListItem>
  );
});

const SortableListContainer = sortableContainer(({ items, onToggle }) => {
  return (
    <List component="div">
      {items.map(({ id, text, checked }, index) => (
        <SortableItem key={id} index={index} text={text} value={checked} onToggle={onToggle} />
      ))}
    </List>
  );
});

/*
 * idLabelAndCheckedTriples should be like: (I think 'id' is arbitrary, so long as it's unique)
 * [
    { id: '0', text: 'Temp', checked: false },
    { id: '1', text: 'Message', checked: true },
  ]
 */

let getTriples = null;
let setTriples = null;

function elevateNode(node) {
  node.style.zIndex = 2;
}

const SortableList = ({ idLabelAndCheckedTriples, onToggle, onReorder }) => {
  const [items, setItems] = useState(idLabelAndCheckedTriples);
  getTriples = items;
  setTriples = setItems;

  useEffect(() => {
    let identical = true;
    idLabelAndCheckedTriples.forEach((triple) => {
      const matchingItem = items.find((item) => item.id === triple.id);
      if (matchingItem.checked !== triple.checked) {
        identical = false;
      }
    });
    if (!identical) {
      setItems(idLabelAndCheckedTriples);
    }
  });

  const onSortEnd = ({ oldIndex, newIndex }) => {
    // const newOrder = arrayMoveImmutable(items, oldIndex, newIndex);
    arrayMoveMutable(items, oldIndex, newIndex);
    onReorder(items);
    setItems(items);
  };

  return (
    <SortableListContainer
      items={items}
      onSortEnd={onSortEnd}
      useDragHandle={true}
      lockAxis="y"
      onToggle={onToggle}
      updateBeforeSortStart={({ node }) => elevateNode(node)}
    />
  );
};

const useColumnChooserStyles = makeStyles((theme) => ({
  root: {
    display: 'inline-block',
    // float: 'right',
    // marginTop: '20px',
  },
  iconButton: {
    paddingTop: 0,
  },
  typography: {
    padding: theme.spacing(1),
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '6px',
    padding: '10px 12px 0',
  },
  selectAllButtonDiv: {
    textAlign: 'center',
    marginBottom: '-12px',
  },
  selectAllButton: {
    textTransform: 'none',
    fontSize: '1.25rem',
  },
  popper: {
    zIndex: 2,
    marginTop: '-6px',
  },
  menuPaper: {
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    boxShadow:
      '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
  },
}));

const ColumnChooser = ({ title, labelAndCheckedPairs, onToggle, onReorder }) => {
  const classes = useColumnChooserStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const idLabelAndCheckedTriples = labelAndCheckedPairs.map((pair, i) => {
    return { id: i, text: pair.name, checked: pair.checked };
  });

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleValueForLabel = (label, checked) => onToggle(label, checked);

  const open = Boolean(anchorEl);

  const allSelected = idLabelAndCheckedTriples.filter((triple) => !triple.checked).length === 0;

  function selectAll() {
    const selected = getTriples.map((triple) => {
      return { ...triple, checked: true };
    });
    selected.forEach((triple) => handleValueForLabel(triple.text, triple.checked));
    setTriples(selected);
  }

  function unselectAll() {
    const unselected = getTriples.map((triple) => {
      return { ...triple, checked: false };
    });
    unselected.forEach((triple) => handleValueForLabel(triple.text, triple.checked));
    setTriples(unselected);
  }

  return (
    <div className={classes.root}>
      <IconButton className={classes.iconButton} onClick={handleIconClick} size="large">
        <ColumnsIcon />
      </IconButton>
      <Popper className={classes.popper} open={open} anchorEl={anchorEl} placement={'bottom-end'}>
        <ClickAwayListener onClickAway={handleClose}>
          <div className={classes.menuPaper}>
            <div className={classes.title}>{title}</div>
            <div className={classes.selectAllButtonDiv}>
              <Button className={classes.selectAllButton} onClick={allSelected ? unselectAll : selectAll}>
                {allSelected ? 'Unselect All' : 'Select All'}
              </Button>
            </div>
            <SortableList
              idLabelAndCheckedTriples={idLabelAndCheckedTriples}
              onToggle={handleValueForLabel}
              onReorder={onReorder}
            />
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const useTileStyles = makeStyles((theme) => ({
  root: {},
  typography: {},
  dragHandleIcon: {
    transform: 'translate(40px, -12px)',
  },
  label: {
    color: 'black',
  },
}));

export default ColumnChooser;
